import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { env } from '@/env';
import { tokenSync } from '@/lib/native-auth';
import {
  agenteListSchema,
  canalListSchema,
  conversaSchema,
  mensagensPageSchema,
  mensagemSchema,
  type Mensagem,
} from './schemas';

const keys = {
  agentes: () => ['agentes'] as const,
  canais: (agente: string) => ['agentes', agente, 'canais'] as const,
  mensagens: (cid: number | string) => ['agentes', 'conversa', String(cid), 'mensagens'] as const,
};

// ── Catálogo + não-lidas ─────────────────────────────────────────────────────
export function useAgentes() {
  return useQuery({
    queryKey: keys.agentes(),
    queryFn: ({ signal }) => api('/api/v2/agentes', { schema: agenteListSchema, signal }),
    staleTime: 10_000,
    refetchInterval: 15_000, // não-lidas aparecem sem reabrir
    refetchIntervalInBackground: true, // WebView do APK: poll mesmo sem foco confiável
  });
}

export function useCanais(agenteId: string | null, enabled = true) {
  return useQuery({
    queryKey: keys.canais(agenteId ?? ''),
    queryFn: ({ signal }) =>
      api(`/api/v2/agentes/${agenteId}/canais`, { schema: canalListSchema, signal }),
    enabled: enabled && !!agenteId,
    staleTime: 10_000,
    refetchInterval: 15_000,
    refetchIntervalInBackground: true,
  });
}

// get-or-create da conversa (em um canal, ou sem canal pro Jão)
export function useAbrirConversa() {
  return useMutation({
    mutationFn: ({ agenteId, canalSlug }: { agenteId: string; canalSlug?: string }) =>
      api(`/api/v2/agentes/${agenteId}/conversa`, {
        method: 'POST',
        body: canalSlug ? { canal_slug: canalSlug } : {},
        schema: conversaSchema,
      }),
  });
}

// ── Mensagens (página mais recente) ──────────────────────────────────────────
export function useMensagens(conversaId: number | null) {
  return useQuery({
    queryKey: keys.mensagens(conversaId ?? ''),
    queryFn: ({ signal }) =>
      api(`/api/v2/agentes/conversas/${conversaId}/mensagens?limit=40`, {
        schema: mensagensPageSchema,
        signal,
      }),
    enabled: conversaId != null,
    staleTime: 2_000,
    refetchInterval: 4_000, // resposta do agente aparece sozinha (poller grava via /resposta)
    refetchIntervalInBackground: true, // WebView do APK: poll mesmo sem foco
  });
}

export function useMarcarLido() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (conversaId: number) =>
      api(`/api/v2/agentes/conversas/${conversaId}/ler`, { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.agentes() }),
  });
}

// ── Envio com streaming (SSE) ────────────────────────────────────────────────
// O api() não streama; aqui usamos fetch direto e parseamos os eventos SSE do
// backend: {tipo:'user_msg'|'token'|'done'|'erro', ...}.
export interface EnviarBody {
  texto?: string;
  tipo?: string;
  anexo_url?: string;
  anexo_meta?: unknown;
  perfil?: 'cliente' | 'tecnico';
}
export interface EnviarHandlers {
  onUserMsg?: (m: Mensagem) => void;
  onToken?: (t: string) => void;
  onDone?: (m: Mensagem) => void;
  onPendente?: () => void; // agente passou do timeout; resposta chega via polling
  onError?: (e: Error) => void;
}

export async function enviarMensagemStream(
  conversaId: number,
  body: EnviarBody,
  handlers: EnviarHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const tk = tokenSync();
  const init: RequestInit = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(tk ? { Authorization: `Bearer ${tk}` } : {}),
    },
    body: JSON.stringify(body),
    ...(signal ? { signal } : {}),
  };
  let res: Response;
  try {
    res = await fetch(`${env.VITE_API_BASE}/api/v2/agentes/conversas/${conversaId}/mensagens`, init);
  } catch (e) {
    handlers.onError?.(e as Error);
    return;
  }
  if (!res.ok || !res.body) {
    handlers.onError?.(new Error(`HTTP ${res.status}`));
    return;
  }

  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let i: number;
    while ((i = buf.indexOf('\n\n')) >= 0) {
      const line = buf.slice(0, i).trim();
      buf = buf.slice(i + 2);
      if (!line.startsWith('data:')) continue;
      let ev: { tipo?: string; token?: string; mensagem?: unknown; error?: string };
      try {
        ev = JSON.parse(line.slice(5).trim()) as {
          tipo?: string;
          token?: string;
          mensagem?: unknown;
          error?: string;
        };
      } catch {
        continue;
      }
      if (ev.tipo === 'user_msg' && ev.mensagem) {
        const p = mensagemSchema.safeParse(ev.mensagem);
        if (p.success) handlers.onUserMsg?.(p.data);
      } else if (ev.tipo === 'token' && typeof ev.token === 'string') {
        handlers.onToken?.(ev.token);
      } else if (ev.tipo === 'done' && ev.mensagem) {
        const p = mensagemSchema.safeParse(ev.mensagem);
        if (p.success) handlers.onDone?.(p.data);
      } else if (ev.tipo === 'pendente') {
        handlers.onPendente?.();
      } else if (ev.tipo === 'erro') {
        handlers.onError?.(new Error(ev.error || 'Falha ao responder'));
      }
    }
  }
}
